import {useEffect} from 'react';
import {isArray, uniq, debounce} from 'lodash-es';
import {getUrlParams, removeSearchParamNoRefresh} from '@/utils/kuQuery';
import KU_QUERY from '@/constants/common/searchParamKeys';

import * as store from '@/store';
import eventBus, {EDITOR_DOM_READY} from '@/eventBus';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import toast, {ToastType, toastObjects} from '@/components/Toast';
import {IConflictTodo} from '@/domains/space/Repository/components/TodoConflictPopper';
import {
    reportOnlineTodoCheckboxClick,
    reportOnlineTodoConvertBtnClick,
    reportOnlineTodoUpdate,
    reportOnlineTodoShow,
    reportTodoCreate,
    reportAutoTransOnlineTodo,
} from '@/buried/todo';

import {TodoDetail, ConflictData, ConflictCallback} from './interface';
import {getChildrenText, scrollIntoView, getMentionContactList} from './utils';

import styles from './index.module.scss';
import {isHi, openInfoFlowBySDK} from '@/utils/browserUtils';

// 待办内容更新的埋点
export const reportUpdateData = (todo: TodoDetail, oldTodo: TodoDetail) => {
    const title = getChildrenText(todo.children);
    const oldTitle = getChildrenText(oldTodo.children);
    if (title !== oldTitle) {
        reportOnlineTodoUpdate('2');
    }

    const users = uniq(getMentionContactList(todo.children)).sort();
    const oldUsers = uniq(getMentionContactList(oldTodo.children)).sort();
    if (users.toString() !== oldUsers.toString()) {
        reportOnlineTodoUpdate('4');
    }

    if (todo.deadlineInfo?.timestamp !== oldTodo.deadlineInfo?.timestamp) {
        reportOnlineTodoUpdate('6');
    }
};

// 监测如流待办的展现次数，用于埋点
const todoShowObserver: any = {
    reportedMap: {},
    paddingList: [],
    report: () => {
        reportOnlineTodoShow(todoShowObserver.paddingList);
        todoShowObserver.paddingList = [];
    },
    debounceReport: debounce(() => {
        todoShowObserver.report();
    }, 300),
    observe() {
        this.report();
        this.reportedMap = {};
        this.disconnect();
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const id = (entry.target as HTMLElement).dataset.id || '';

                    if (this.reportedMap[id]) {
                        return;
                    }

                    if (id) {
                        this.paddingList.push(id);
                        this.debounceReport();
                        this.reportedMap[id] = true;
                    }
                }
            });
        }, {threshold: [1]});

        const editorContainer = document.querySelector('#doc-editor') as HTMLElement;
        if (editorContainer) {
            const elList = editorContainer.querySelectorAll('[data-online-todo-id]');
            if (elList.length > 0) {
                elList.forEach(el => {
                    this.observer.observe(el);
                });
            }
        }
    },
    delayObserve() {
        this.delayObserveTimeout = window.setTimeout(this.observe.bind(this), 600);
    },
    disconnect() {
        window.clearTimeout(this.delayObserveTimeout);
        this.observer && this.observer.disconnect();
        this.delayObserveTimeout = null;
    },
};

function onTodoRemove(uuids: string[]) {
    const conflictList: IConflictTodo[] = store.getData(store.KEYS.TODO_CONFLICT_LIST) || [];
    store.setTodoConflictList(conflictList.filter(item => !uuids.includes(item.id)));
}

function createSuccessToast(todoId: string, jumpUrl: {pc: string, mobile: string}) {
    function jumpInfoflow() {
        if (!todoId) {
            return;
        }
        if (isHi()) {
            const url =
                process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.online ||
                process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.gray
                    ? `https://work.infoflow.baidu.com/workflow/web/todo?todoId=${todoId}`
                    : `http://st01-ruliu-qa03.st01.baidu.com:8244/workflow/web/todo?todoId=${todoId}`;
            openInfoFlowBySDK(url, 'BDHI_GROUP_ID_TODO');
        } else if (jumpUrl.pc) {
            window.open(jumpUrl.pc, '_blank');
        }
    }

    toast({
        type: ToastType.Success,
        duration: 3000,
        content: (
            <div className={styles.tips}>
                {'已成功与如流待办同步数据，可以跳转至如流 > 工作流中查看详情'}
                <span className={styles.btn} onClick={jumpInfoflow}>
                    点击跳转
                </span>
            </div>
        ),
    });
}

function isInViewPort(el: HTMLElement) {
    const elRect = el.getBoundingClientRect();
    return elRect.bottom > 10 && elRect.top < window.innerHeight;
}

const handleConflict = (id: string, conflictData: any, todoEl: HTMLElement, cbObj: ConflictCallback) => {
    function confirm() {
        toastObjects[ToastType.Error]?.remove();
        cbObj.onConflictConfirm();
        const conflictList: IConflictTodo[] = store.getData(store.KEYS.TODO_CONFLICT_LIST) || [];
        store.setTodoConflictList(conflictList.filter(item => item.id !== id));
    }

    function cancel() {
        toastObjects[ToastType.Error]?.remove();
        cbObj.onConflictCancel();
    }

    function viewDetail() {
        toastObjects[ToastType.Error]?.remove();
        cbObj.onConflictDetail();
    }

    const conflictList: IConflictTodo[] = store.getData(store.KEYS.TODO_CONFLICT_LIST) || [];
    store.setTodoConflictList([
        ...conflictList,
        {
            id,
            ...conflictData,
            confirm: async () => {
                return confirm();
            },
            cancel: cancel,
            closeToast: () => toastObjects[ToastType.Error]?.remove(),
        },
    ]);

    if (todoEl && !isInViewPort(todoEl)) {
        toast({
            type: ToastType.Error,
            duration: 0,
            manualClose: true,
            className: styles.toast,
            content: (
                <div className={styles.conflictTips}>
                    待办中的内容发生冲突，是否要进行内容的更新覆盖？
                    <span className={styles.viewBtn} onClick={viewDetail}>
                        查看详情
                    </span>
                    <span className={styles.splitLine}>|</span>
                    <span className={styles.coverBtn} onClick={confirm}>
                        直接覆盖
                    </span>
                </div>
            ),
        });
    }
};

export const useOnlineTodoConfig = () => {
    return {
        open: true,
        handleConflict(data: ConflictData, cbObj: ConflictCallback) {
            handleConflict(data.id, data.conflictData, data.todoEl, cbObj);
        },
        onCreateTodo() {
            reportOnlineTodoConvertBtnClick();
        },
        onCreateTodoSuccess() {
            todoShowObserver.delayObserve();
        },
        handleCreateSuccessToast(id: string, jumpUrl: any) {
            createSuccessToast(id, jumpUrl);
        },
        onGetTodoDetail() {
            todoShowObserver.observe();
        },
        onUpdateChecked(id: string, todoId: string, checked: boolean) {
            // reportOnlineTodoCheckboxClick(checked);
        },
        onUpdateData(
            id: string,
            todoId: string,
            oldTodo: TodoDetail,
            todo: TodoDetail,
            options?: {
                autoUpdateByMentionDoc: boolean;
            }
        ) {
            // reportUpdateData(todo, oldTodo);
        },
        onDelReference(todoIdMap: Map<string, string[]>) {
            const obj = Object.fromEntries(todoIdMap.entries());
            const uuids = Object.values(obj).flat();
            onTodoRemove(uuids);
        },
    };
};

const onlineTodoIds = getUrlParams(KU_QUERY.document.onlineTodoId);
let onlineTodoId = isArray(onlineTodoIds) ? onlineTodoIds[0] : onlineTodoIds;
function scrollToOnlineTodo() {
    setTimeout(() => {
        if (!window.isTocPage && onlineTodoId) {
            const result: boolean = scrollIntoView('', onlineTodoId || '');
            if (result === false) {
                toast({
                    type: ToastType.Error,
                    duration: 3000,
                    content: '当前待办已被删除',
                });
            }

            onlineTodoId = '';
            removeSearchParamNoRefresh(KU_QUERY.document.onlineTodoId);
        }
    }, 600);
}

export const useTodoPosition = () => {
    useEffect(() => {
        eventBus.on(EDITOR_DOM_READY, scrollToOnlineTodo);

        return () => {
            eventBus.off(EDITOR_DOM_READY, scrollToOnlineTodo);
        };
    }, []);
};

// 创建普通待办的埋点
const newTodoIds: {[id: string]: boolean} = {};
export const useTodoConfig = () => {
    return {
        autoTransOnlineTodo: true,
        onBatchCreateTodoSuccess(todos: any) {
            if (todos && Object.keys(todos).length) {
                reportAutoTransOnlineTodo(Object.keys(todos));
            }
        },
        reportCallback(options: {type: string, todoIds: string[]}) {
            const {type, todoIds = []} = options;
            if (type === 'update') {
                todoIds.forEach(id => {
                    if (newTodoIds[id]) {
                        reportTodoCreate();
                        delete newTodoIds[id];
                    }
                });
            } else if (type === 'create') {
                todoIds.forEach(id => {
                    newTodoIds[id] = true;
                });
            }
        },
    };
};
